import { Button } from "primereact/button";
import { ConfirmDialog } from "primereact/confirmdialog";
import { createContext, useEffect, useRef, useState } from "react";
import QUESTION_TYPE from "../../../constants/question-type";
import { getCurrentUserFromLocalStorage } from "../../../helpers/localStorageHelper";
import { getQuiz, getQuizQuestionDetail, getQuizResultDetail, postFreeQuizAnswer } from "../../../services/quizService";
import AnswerSheet from "./AnswerSheet";
import TestQuestion from "./TestQuestion";
import TestTimer from "./TestTimer";
import TopikTestResult from "./TopikTestResult";

export const TopikTestContext = createContext({
  selectedAnswer: {},
  onSelectAnswer: () => {},
  user: {},
  translation: {},
});

export default function TakeTest({ props }) {
  const [quiz, setQuiz] = useState(null);
  const [questionDetail, setQuestionDetail] = useState([]);
  const [questions, setQuestion] = useState([]);
  const [selectedAnswer, setSelectedAnswer] = useState({});
  const [user] = useState(() => {
    const currentUser = getCurrentUserFromLocalStorage();
    return currentUser;
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const timer = useRef(null);

  const startTimeRef = useRef(null);

  const [currentResultData, setCurrentResultData] = useState({});

  const [showConfirm, setShowConfirm] = useState(false);

  const mapQuestion = (questionGroup) => {
    if (questionGroup.length === 0) return [];

    return questionGroup.reduce((acc, item) => {
      const isWriting = item.questionGroupType?.code === QUESTION_TYPE.WRITING;
      const questions = item.questions.map((q) => ({
        ...q,
        answers: isWriting ? [] : q.answers,
      }));
      return [...acc, ...questions];
    }, []);
  };

  const handleUserAnswer = ({ answerId, questionId, userAnswer }) => {
    if (isSubmitted) return;
    if (userAnswer?.length) {
      setSelectedAnswer((prev) => ({
        ...prev,
        [questionId]: userAnswer,
      }));
      return;
    }
    if (selectedAnswer[questionId] === answerId) return;
    setSelectedAnswer((prev) => ({
      ...prev,
      [questionId]: answerId,
    }));
  };

  const handleSubmit = async () => {
    setIsSubmitted(true);
    const answerRequestData = questionDetail?.flatMap((questionGroup) => {
      return questionGroup?.questions?.map((questionItem) => ({
        ...questionItem,
        userAnswer: selectedAnswer[questionItem.id] ?? "",
        answers: questionItem?.answers?.map((answer) => ({
          ...answer,
          userSelect: selectedAnswer[questionItem.id] === answer.id,
        })),
      }));
    });

    const endDoingTime = Date.now();
    const requestData = {
      answerData: answerRequestData,
      isAnonymous: user?.id ? false : true,
      testTime: Math.floor((endDoingTime - startTimeRef.current) / 1000),
    };
    const data = await postFreeQuizAnswer(props?.selectedTest?.quizId, requestData);
    if (quiz?.quizCategory === QUESTION_TYPE.WRITING) {
      props?.setTakeTestStep(2);
      return;
    }
    if (data !== null) {
      setCurrentResultData({ ...data });
      await getResultDetail(data.id);
    }
  };

  const getResultDetail = async (resultId) => {
    const response = await getQuizResultDetail(resultId);
    const newQuestion = questionDetail?.map((questionGroup) => {
      return {
        ...questionGroup,
        questions: questionGroup?.questions?.map((question) => {
          const questionResult = response?.results?.find((item) => item?.id === question?.id);
          return {
            ...question,
            correctAnswerIndex: questionResult?.correctAnswerIndex,
            answers: [...(questionResult?.answers ?? [])],
            userAnswer: questionResult?.userAnswer,
          };
        }),
      };
    });
    setQuestionDetail(newQuestion);
    const questionsMap = mapQuestion(newQuestion);
    setQuestion(questionsMap);
  };

  const handleTimeUp = () => {
    props?.setTakeTestStep(1);
    setShowConfirm(true);
  };

  useEffect(() => {
    if (props?.takeTestStep === 1) {
      handleSubmit();
    }
  }, [props?.takeTestStep]);

  useEffect(() => {
    const getQuizDetail = async () => {
      const response = await getQuiz(props?.selectedTest?.quizId);
      setQuiz(response);
    };

    const getQuizData = async () => {
      const response = await getQuizQuestionDetail(props?.selectedTest?.quizId);
      setQuestionDetail(response);
      const questionsMap = mapQuestion(response);
      setQuestion(questionsMap);

      //Set start doing time
      startTimeRef.current = Date.now();
    };
    getQuizDetail();
    getQuizData();
  }, []);

  return (
    <TopikTestContext.Provider
      value={{
        selectedAnswer,
        onSelectAnswer: handleUserAnswer,
        user,
        translation: props,
      }}
    >
      <ConfirmDialog
        className="topik-test-timeup-dialog"
        visible={showConfirm}
        onHide={() => setShowConfirm(false)}
        message={props?.timeUpWarningTitle}
        footer={
          <div className="flex justify-content-end p-3">
            <Button className="keduall-buttons button-filled-default-normal-primary" onClick={() => setShowConfirm(false)}>
              <p className="keduall-typography text-16-semibold-600 white-color">{props?.nextButtonTitle}</p>
            </Button>
          </div>
        }
      />
      {props?.takeTestStep !== 2 ? (
        <>
          {quiz?.quizDuration && props?.takeTestStep === 0 ? (
            <div className="mb-5">
              <TestTimer duration={quiz?.quizDuration} ref={timer} isSubmitted={isSubmitted} handleTimeUp={handleTimeUp} />
            </div>
          ) : null}

          <section>
            <div>
              <div>{questionDetail.length !== 0 ? <TestQuestion questionGroups={questionDetail} isSubmitted={isSubmitted} /> : null}</div>
            </div>
          </section>
          <div className="flex justify-content-end">
            {questions.length !== 0 && quiz?.quizSkillsCode !== QUESTION_TYPE.WRITING ? (
              <AnswerSheet questions={questions} quizId={props?.selectedTest?.quizId} isSubmitted={isSubmitted} />
            ) : null}
          </div>
        </>
      ) : (
        <div>
          <TopikTestResult props={{ ...props, currentResultData, quiz, selectedTopikLevelName: props?.selectedTopikLevelName }} />
        </div>
      )}
    </TopikTestContext.Provider>
  );
}
